import {
  type UFOExperience,
  ExperienceTypes,
  ExperiencePerformanceTypes,
} from '@atlassian/ufo';
import { type CustomData } from '@atlassian/ufo-experimental';
import React, { useCallback, useMemo } from 'react';

import { type MetricNameValue } from './metrics';
import { ReliabilityMetrics } from './reliability-metrics';

export interface PageLoadExperience {
  experience: UFOExperience;
  onExperienceFailure: (reason: string, data?: CustomData) => void;
  onExperienceSuccess: VoidFunction;
  id: string;
}

const PageLoadExperienceContext = React.createContext<PageLoadExperience | null>(null);

export const PageLoadExperienceProvider: React.FC<
  React.PropsWithChildren<{
    id: MetricNameValue;
  }>
> = ({ id, children }) => {
  const experience = useMemo(() => {
    const ufoExperience = ReliabilityMetrics.getInstance().start(id, {
      type: ExperienceTypes.Load,
      performanceType: ExperiencePerformanceTypes.PageLoad,
    });

    return ufoExperience;
  }, [id]);

  const onExperienceFailure = useCallback(
    (reason: string, data?: CustomData) => {
      experience.failure({
        metadata: { reason, file: 'use-page-load-experience.tsx', ...data },
      });
    },
    [experience]
  );

  const onExperienceSuccess = useCallback(() => {
    experience.success();
  }, [experience]);

  const fabricExperience = useMemo(
    () => ({
      onExperienceFailure,
      onExperienceSuccess,
      experience,
      id,
    }),
    [onExperienceFailure, onExperienceSuccess, experience, id]
  );

  return (
    <PageLoadExperienceContext.Provider value={fabricExperience}>
      {children}
    </PageLoadExperienceContext.Provider>
  );
};

export const usePageLoadExperience = (): PageLoadExperience => {
  return React.useContext(PageLoadExperienceContext) as PageLoadExperience;
};

export interface TrackedExperienceProps {
  id: string;
}
